import {
  SCRIPTED_AGENT_ID,
  ScriptNode,
  type Script,
} from '@/config/scripts/prudential-annual-checkin';
import { CUSTOMER_NAME as GRAB_DEFAULT_CUSTOMER_NAME } from '@/config/scripts/grab-restaurant-onboarding';
import { getScript, scriptRegistry } from '@/config/scripts';
import { getScriptPrompt } from '@/config/scripts/prompts';
import api from '@/utils/ky';
import { ScriptEngine, ScriptState } from '@/utils/scriptEngine';
import { playScriptFirstMessage } from '@/utils/tts';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CallMode, CallType } from '../routes/agent/RolePlay';
import {
  CallState,
  LoadingState,
  AccentType,
  type Transcript,
  type BaseAgentCallProps,
  type AgentCallInterface,
} from './types/agentCall.types';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from 'livekit-client';

// Voice ID mapping (LiveKit specific)
const VOICE_IDS = {
  [AccentType.BRITISH]: 'british-voice-id', // TODO: Replace with actual LiveKit voice IDs
  [AccentType.IRISH]: 'irish-voice-id', // TODO: Replace with actual LiveKit voice IDs
};

const personalizeGrabCustomerName = (
  script: Script,
  newName: string,
): Script => {
  const replaceName = (text?: string) => {
    if (!text) return text;
    return text
      .replace(new RegExp(GRAB_DEFAULT_CUSTOMER_NAME, 'g'), newName)
      .replace(
        new RegExp(GRAB_DEFAULT_CUSTOMER_NAME.toLowerCase(), 'g'),
        newName.toLowerCase(),
      );
  };

  const replaceArray = (values?: string[]) =>
    values?.map((value) => replaceName(value) || value);

  const updatedNodes = Object.fromEntries(
    Object.entries(script.nodes).map(([nodeId, node]) => {
      const updatedExpectedResponse = node.expectedResponse
        ? {
            positive: replaceArray(node.expectedResponse.positive),
            negative: replaceArray(node.expectedResponse.negative),
            neutral: replaceArray(node.expectedResponse.neutral),
          }
        : undefined;

      return [
        nodeId,
        {
          ...node,
          agentMessage: replaceName(node.agentMessage) || node.agentMessage,
          expectedResponse: updatedExpectedResponse,
        },
      ];
    }),
  );

  return {
    ...script,
    nodes: updatedNodes as Script['nodes'],
  };
};

export const useLivekitAgentCall = ({
  productCode,
  callType,
  accent = AccentType.DEFAULT,
  mode = CallMode.NORMAL,
  scriptId = 'prudential-annual-checkin',
  parentId,
  userId,
  onClientInfoNeeded,
}: BaseAgentCallProps): AgentCallInterface => {
  const [searchParams] = useSearchParams();
  const customCustomerName = searchParams.get('name')?.trim() || null;
  const configPreset = searchParams.get('config')?.trim() || null;
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<any>(null);
  const [clientInfo, setClientInfo] = useState<any>({});
  const [product, setProduct] = useState<any>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<any>(null);

  // Transcript state
  const [transcript, setTranscript] = useState<Transcript[]>([]);

  // Script engine state
  const scriptEngineRef = useRef<ScriptEngine | null>(null);
  const [scriptState, setScriptState] = useState<ScriptState | null>(null);
  const [currentScriptNode, setCurrentScriptNode] = useState<ScriptNode | null>(
    null,
  );

  // LiveKit-specific state
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const livekitRoomRef = useRef<any>(null);
  const livekitConnectionRef = useRef<any>(null);

  useEffect(() => {
    if (productCode) {
      initializeProduct();
    }
  }, [productCode]);

  // Set agent info for scripted mode immediately when component loads
  useEffect(() => {
    if (mode === CallMode.SCRIPTED && scriptId) {
      const currentScript = getScript(scriptId);
      const scriptAgentInfo = currentScript?.agentInfo;
      const scriptMetadata = scriptRegistry[scriptId]?.metadata;
      setAgentInfo({
        profile: {
          name: scriptAgentInfo?.name || 'Rachel',
          role: 'Virtual Assistant',
          company: scriptMetadata?.company || 'Prudential',
          avatar:
            scriptAgentInfo?.avatar ||
            'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
        },
      });
    }
  }, [mode, scriptId]);

  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const initializeProduct = async () => {
    try {
      setLoadingState(LoadingState.LOADING_PRODUCT);

      // Fetch product details
      const productData: any = await api
        .get(`api/agent/products/${productCode}`)
        .json();

      setProduct(productData.product);
      setLoadingState(LoadingState.READY);
    } catch (error) {
      console.error('Error initializing product:', error);
      setError('Failed to load product information. Please try again.');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);

      // Initialize script engine if in scripted mode
      if (mode === CallMode.SCRIPTED) {
        const script = getScript(scriptId || 'prudential-annual-checkin');
        if (!script) {
          throw new Error(`Script not found: ${scriptId}`);
        }

        const personalizedScript =
          scriptId === 'grab-restaurant-onboarding' && customCustomerName
            ? personalizeGrabCustomerName(script, customCustomerName)
            : script;

        scriptEngineRef.current = new ScriptEngine(
          personalizedScript,
          (state) => {
            setScriptState(state);
            setCurrentScriptNode(state.currentNode);
          },
        );

        const initialNode = scriptEngineRef.current.getCurrentNode();
        setCurrentScriptNode(initialNode);
      }

      // Create session with script data
      const sessionData: any = await api
        .post('api/agent/sessions', {
          json: {
            userId,
            productCode,
            callType,
            mode,
            scriptId: mode === CallMode.SCRIPTED ? scriptId : undefined,
            script: scriptEngineRef.current?.getScript(), // Send full script to backend
            ...(parentId && { parentSessionId: parentId }),
            ...(configPreset && { config: configPreset }), // Add config preset if specified
          },
        })
        .json();

      setSessionId(sessionData.sessionId);

      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const session: any = await api
        .get(`api/agent/sessions/${sessionData.sessionId}`)
        .json();

      // Set client info if available (for follow-up calls)
      if (session.clientInfo) {
        setClientInfo(session.clientInfo);
      }

      // Override agent info for scripted mode
      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const currentScript = scriptEngineRef.current.getScript();
        const scriptAgentInfo = currentScript?.agentInfo;
        const scriptMetadata =
          scriptRegistry[scriptId || 'prudential-annual-checkin']?.metadata;
        setAgentInfo({
          profile: {
            name: scriptAgentInfo?.name || 'Rachel',
            role: 'Virtual Assistant',
            company: scriptMetadata?.company || 'Prudential',
            avatar:
              scriptAgentInfo?.avatar ||
              'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
          },
        });
      } else {
        setAgentInfo(session.persona);
      }

      // Determine voice ID based on mode and accent
      let voiceId: string;
      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const currentScript = scriptEngineRef.current.getScript();
        voiceId = currentScript?.agentInfo?.voiceId || 'default-voice-id';
      } else if (accent !== AccentType.DEFAULT) {
        voiceId = VOICE_IDS[accent];
      } else {
        voiceId = session.aiConfig.voiceId;
      }

      // Prepare agent prompt based on mode
      let agentPrompt = session.voiceInstructions.chat;
      let firstMessage = session.aiConfig.initialPrompt;

      if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
        const scriptMessage = scriptEngineRef.current.getScriptForAgent();
        const currentScript = scriptEngineRef.current.getScript();
        const scriptNodes = currentScript?.nodes || {};

        agentPrompt = getScriptPrompt(
          scriptId || 'prudential-annual-checkin',
          scriptMessage,
          scriptNodes,
        );

        firstMessage = scriptMessage;

        // Add contextual response capability to the prompt
        agentPrompt += `\n\nCONTEXTUAL RESPONSES:
When customer asks off-script questions, respond as a professional insurance advisor:
- Keep responses brief and relevant
- After answering, ALWAYS return to the current script step
- Do NOT move to a different script node after answering a question
- Example: If at Step 2 and customer asks "What will we discuss?", answer the question then continue waiting for their response to Step 2's question`;
      }

      const agentId =
        mode === CallMode.SCRIPTED
          ? scriptId === 'grab-restaurant-onboarding'
            ? 'agent_2401k6mgpxsff1wt1zk4h5vs8rf7'
            : SCRIPTED_AGENT_ID
          : session.aiConfig.agentId;

      console.log('Using agent prompt:', agentPrompt);
      console.log('Using first message:', firstMessage);
      console.log('Using voiceId:', voiceId);
      console.log('Using agentId:', agentId);

      // Set call to active state and start timer
      startTimeRef.current = Date.now();
      setLoadingState(LoadingState.ACTIVE);
      setCallState(CallState.ACTIVE);

      // Play TTS first for WRISE script
      if (mode === CallMode.SCRIPTED && scriptId === 'wealth-landscape-forum') {
        try {
          const wriseMessage = `Hi Good Evening ${
            clientInfo?.name || '[Jean]'
          }. This is Emma calling from WRISE Prestige Hong Kong. I'm a virtual assistant to your advisor Stephen, and I hope to share some info on his behalf today. Before I begin, may I highlight that this call is recorded for quality assessment purposes.`;

          // Add TTS message to transcript
          setTranscript((prev) => [
            ...prev,
            {
              source: 'ai',
              message: wriseMessage,
              timestamp: new Date(),
            },
          ]);

          await playScriptFirstMessage(wriseMessage, 'nNMKe8xS6OJl0wUb8Zss');
          console.log('WRISE TTS first message played successfully');
        } catch (error) {
          console.error('Failed to play WRISE TTS first message:', error);
        }
      }

      // Initialize LiveKit connection
      const room = new Room();
      livekitRoomRef.current = room;

      // Set up event listeners for audio tracks
      room.on(
        RoomEvent.TrackSubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            // Attach audio track to play agent's voice
            const audioElement = track.attach();
            document.body.appendChild(audioElement);
            audioElement.play();
            console.log('Agent audio track attached', participant.identity);
          }
        },
      );

      // Handle track unsubscribed
      room.on(
        RoomEvent.TrackUnsubscribed,
        (
          track: RemoteTrackPublication['track'],
          _publication: RemoteTrackPublication,
          participant: RemoteParticipant,
        ) => {
          if (track?.kind === Track.Kind.Audio) {
            track.detach().forEach((element) => element.remove());
            console.log('Agent audio track detached', participant.identity);
          }
        },
      );

      // Handle transcription from LiveKit agent
      room.on(
        RoomEvent.TranscriptionReceived,
        (segments: any[], participant?: RemoteParticipant) => {
          console.log('📝 Transcription received:', {
            segmentCount: segments.length,
            participantIdentity: participant?.identity,
            segments: segments.map((s) => ({
              id: s.id,
              text: s.text,
              final: s.final,
            })),
          });

          segments.forEach((segment) => {
            // Only process segments with sufficient length or final segments
            if (segment.text.length > 3 || segment.final) {
              // Only add final transcriptions to avoid word-by-word updates
              if (segment.final) {
                const isAgent = participant?.identity?.startsWith('agent-');

                console.log('📝 Final transcription:', {
                  isAgent,
                  text: segment.text,
                  participantIdentity: participant?.identity,
                });

                setTranscript((prev) => [
                  ...prev,
                  {
                    source: isAgent ? 'ai' : 'user',
                    message: segment.text,
                    timestamp: new Date(),
                  },
                ]);

                // Handle script state if in scripted mode and it's a user message
                if (mode === CallMode.SCRIPTED && !isAgent && scriptEngineRef.current) {
                  scriptEngineRef.current.processCustomerResponse(segment.text);
                }
              }
            }
          });
        },
      );

      // Handle agent messages via data channel
      room.on(
        RoomEvent.DataReceived,
        (payload: Uint8Array, _participant?: RemoteParticipant) => {
          try {
            const message = JSON.parse(new TextDecoder().decode(payload));
            console.log('Received message:', message);

            // Add to transcript if not already handled by transcription
            if (message.text || message.message) {
              const text = message.text || message.message;
              setTranscript((prev) => {
                // Avoid duplicates - check if last message is the same
                const lastMsg = prev[prev.length - 1];
                if (lastMsg && lastMsg.message === text &&
                    lastMsg.source === (message.source || 'ai')) {
                  return prev;
                }

                return [
                  ...prev,
                  {
                    source: message.source || 'ai',
                    message: text,
                    timestamp: new Date(),
                  },
                ];
              });
            }

            // Update speaking state
            if (message.speaking !== undefined) {
              setIsSpeaking(message.speaking);
            }

            // Handle script state if in scripted mode
            if (mode === CallMode.SCRIPTED && scriptEngineRef.current) {
              // Process customer responses for script progression
              if (message.source === 'user') {
                scriptEngineRef.current.processCustomerResponse(
                  message.text || message.message,
                );
              }
            }
          } catch (error) {
            console.error('Error parsing data message:', error);
          }
        },
      );

      // Handle connection state changes
      room.on(RoomEvent.Connected, () => {
        console.log('Connected to LiveKit room');
      });

      room.on(RoomEvent.Disconnected, () => {
        console.log('Disconnected from LiveKit room');
      });

      room.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to LiveKit room...');
      });

      room.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to LiveKit room');
      });

      // Handle participant speaking state via attributes (preferred for LiveKit agents)
      room.on(RoomEvent.ParticipantAttributesChanged, (changedAttributes: Record<string, string>, participant: RemoteParticipant) => {
        console.log('🔄 Participant attributes changed:', {
          participantIdentity: participant.identity,
          changedAttributes,
        });

        if (participant.identity.startsWith('agent-')) {
          const agentState = changedAttributes['lk.agent.state'];
          const nowSpeaking = agentState === 'speaking';

          console.log('🤖 Agent speaking state change:', {
            participantIdentity: participant.identity,
            agentState,
            nowSpeaking,
          });

          setIsSpeaking(nowSpeaking);
        }
      });

      // Handle participant speaking state via metadata (fallback)
      room.on(RoomEvent.ParticipantMetadataChanged, (metadata, _participant) => {
        try {
          const meta = metadata ? JSON.parse(metadata) : {};
          if (meta.speaking !== undefined) {
            console.log('🤖 Metadata speaking state:', meta.speaking);
            setIsSpeaking(meta.speaking);
          }
        } catch (error) {
          console.error('Error parsing participant metadata:', error);
        }
      });

      // Get LiveKit token from backend (same endpoint as useLiveKit hook)
      const tokenResponse = await api
        .get('livekit/token', {
          searchParams: {
            username: userId || 'anonymous',
            room: sessionData.sessionId,
            livekitAgentName: import.meta.env.VITE_LIVEKIT_CALL_AGENT_NAME || 'LIVEKIT-DEMO-DEV-CALL-AGENT',
            ...(configPreset && { config: configPreset }),
          },
        })
        .json<{ token: string }>();

      const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

      if (!livekitUrl) {
        throw new Error('VITE_LIVEKIT_URL environment variable is not set');
      }

      console.log('Connecting to LiveKit:', { url: livekitUrl, room: sessionData.sessionId });

      // Connect to LiveKit room
      await room.connect(livekitUrl, tokenResponse.token);
      console.log('Successfully connected to LiveKit room');

      // Enable microphone
      await room.localParticipant.setMicrophoneEnabled(true);
      console.log('Microphone enabled');

    } catch (error) {
      console.error('Error starting call:', error);
      setError('Failed to start call session');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      // Disconnect LiveKit session
      if (livekitRoomRef.current) {
        // Disable microphone before disconnecting
        await livekitRoomRef.current.localParticipant.setMicrophoneEnabled(
          false,
        );
        // Disconnect from room
        await livekitRoomRef.current.disconnect();
        console.log('Disconnected from LiveKit room');
        livekitRoomRef.current = null;
      }

      // Only make backend call if we have a valid session ID
      if (sessionId) {
        const formattedTranscript = transcript
          .map(
            (msg) =>
              `${msg.source === 'ai' ? 'Agent' : 'User'}: ${msg.message}`,
          )
          .join('\n');

        await api.post(`api/agent/sessions/${sessionId}/end`, {
          json: {
            transcript: formattedTranscript,
            duration: (Date.now() - startTimeRef.current) / 1000,
          },
        });
      }

      // End call
      setCallState(CallState.ENDED);

      // For scripted mode, delay showing thank you dialog to let AI finish speaking
      if (mode === CallMode.SCRIPTED) {
        // The parent component will handle showing the thank you dialog
        if (onClientInfoNeeded) {
          setTimeout(() => {
            onClientInfoNeeded();
          }, 3000); // 3 second delay
        }
      } else if (callType === CallType.INITIAL && onClientInfoNeeded) {
        // If this was an initial call in normal mode, show the client info form
        onClientInfoNeeded();
      } else {
        // Show end call notification for other cases
        toast.success('Call ended successfully');
      }
    } catch (error) {
      console.error('Error ending call:', error);

      // Still end the call state even if backend fails
      setCallState(CallState.ENDED);

      // For scripted mode, show thank you dialog after delay
      if (mode === CallMode.SCRIPTED && onClientInfoNeeded) {
        setTimeout(() => {
          onClientInfoNeeded();
        }, 3000);
      } else {
        toast.error('Call ended');
      }
    } finally {
      setLoadingState(LoadingState.READY);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const saveClientInfo = async (
    clientInfoData: any,
    followupDate?: string,
    followupTime?: string,
    followupNotes?: string,
  ) => {
    try {
      const data = {
        ...clientInfoData,
        followupScheduled:
          followupDate && followupTime
            ? new Date(`${followupDate}T${followupTime}`).toISOString()
            : undefined,
        followupNotes,
      };

      // Include accent parameter in the follow-up URL
      const accentParam =
        accent !== AccentType.DEFAULT ? `&accent=${accent}` : '';

      // Save client info
      const response: any = await api
        .post(`api/agent/sessions/${sessionId}/client-info`, {
          json: {
            clientInfo: data,
            additionalParams: accentParam,
          },
        })
        .json();

      toast.success('Follow-up information saved successfully');

      return response.followupUrl;
    } catch (error) {
      console.error('Error saving client info:', error);
      toast.error('Failed to save client information');
      throw error;
    }
  };

  return {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    clientInfo,
    product,
    isMuted,
    callDuration,
    transcript,
    isSpeaking,
    accent,
    currentScriptNode,
    scriptState,
    handleAnswerCall,
    handleEndCall,
    setClientInfo,
    saveClientInfo,
    formatTime,
    retryInitialization: initializeProduct,
  };
};
